import React, { useEffect, useState, useMemo } from 'react'
import { Calendar, MapPin, Clock, Loader2, ArrowRight, Users, School } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import type { NavigationParams } from '../App'
import { sansJpa, stagesDesJpa } from '../../lib/jpa/stages.supabase'
import { dateEnClair, heureBruxelles } from '../../lib/garderie/pointage'
import { garderiesDuJour, type ContexteDuJour, type GarderieDuJour } from '../../lib/garderie/raccourcis'
import { lireLesRaccourcisDeLaGarderie } from '../../lib/garderie/raccourcis.supabase'

interface SessionTile {
  centerId: string
  centerName: string
  periode: string
  semaine: string
  startDate: string
  endDate: string
  status: 'en_cours' | 'terminee' | 'a_venir'
  sessionCount: number
}

interface DashboardProps {
  onNavigate?: (page: string, params?: NavigationParams) => void
}

function getRelevantDateRange(): { ranges: Array<{ start: string; end: string; status: 'en_cours' | 'terminee' | 'a_venir' }> } {
  const today = new Date()
  const dayOfWeek = today.getDay() // 0=Sun, 1=Mon...6=Sat

  const isWeekend = dayOfWeek === 0 || dayOfWeek === 6

  const ranges: Array<{ start: string; end: string; status: 'en_cours' | 'terminee' | 'a_venir' }> = []

  if (isWeekend) {
    // Current/just-ended week (Mon-Fri)
    const monday = new Date(today)
    const diff = dayOfWeek === 0 ? -6 : -(dayOfWeek - 1)
    monday.setDate(today.getDate() + diff)
    const friday = new Date(monday)
    friday.setDate(monday.getDate() + 4)
    ranges.push({
      start: toDateStr(monday),
      end: toDateStr(friday),
      status: 'terminee',
    })

    // Next week (upcoming Mon-Fri)
    const nextMonday = new Date(monday)
    nextMonday.setDate(monday.getDate() + 7)
    const nextFriday = new Date(nextMonday)
    nextFriday.setDate(nextMonday.getDate() + 4)
    ranges.push({
      start: toDateStr(nextMonday),
      end: toDateStr(nextFriday),
      status: 'a_venir',
    })
  } else {
    // Weekday: show current week
    const monday = new Date(today)
    monday.setDate(today.getDate() - (dayOfWeek - 1))
    const friday = new Date(monday)
    friday.setDate(monday.getDate() + 4)
    ranges.push({
      start: toDateStr(monday),
      end: toDateStr(friday),
      status: 'en_cours',
    })
  }

  return { ranges }
}

function toDateStr(d: Date): string {
  return d.toISOString().split('T')[0]
}

function formatShortDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00')
  return d.toLocaleDateString('fr-BE', { day: '2-digit', month: '2-digit' })
}

export function Dashboard({ onNavigate }: DashboardProps) {
  const { userProfile } = useAuth()
  const [tiles, setTiles] = useState<SessionTile[]>([])
  const [loading, setLoading] = useState(true)
  /*
    La garderie des écoles (02/11/2026) : les jours d'école, une tuile par école
    qui ouvre la liste du jour sur la plage de l'heure. Administration et
    coordination : la liste se lit avec leurs droits.
  */
  const gereLaGarderie = userProfile?.role === 'admin' || userProfile?.role === 'coordi'
  const [garderie, setGarderie] = useState<ContexteDuJour | null>(null)
  const [heure, setHeure] = useState(() => heureBruxelles())

  useEffect(() => {
    fetchSessionTiles()
  }, [])

  useEffect(() => {
    if (!gereLaGarderie) return
    let annule = false
    lireLesRaccourcisDeLaGarderie()
      .then((r) => {
        if (!annule) setGarderie(r.contexte)
      })
      .catch((err) => console.error('Raccourcis de la garderie illisibles :', err))
    /* La plage suit l'heure : le matin, puis l'ATL. */
    const minuterie = window.setInterval(() => setHeure(heureBruxelles()), 60_000)
    return () => {
      annule = true
      window.clearInterval(minuterie)
    }
  }, [gereLaGarderie])

  const garderies = useMemo<GarderieDuJour[]>(() => (garderie ? garderiesDuJour({ ...garderie, heure }) : []), [garderie, heure])

  const ouvrirLaGarderie = (g: GarderieDuJour) => {
    if (!onNavigate || !garderie) return
    /* L'heure du geste, pas celle du dernier rafraîchissement. */
    const maintenant = garderiesDuJour({ ...garderie, heure: heureBruxelles() }).find((x) => x.centerId === g.centerId) ?? g
    onNavigate('garderie-pointage', { centerId: g.centerId, date: maintenant.date, plage: maintenant.plage.code })
  }

  const fetchSessionTiles = async () => {
    setLoading(true)
    try {
      const { ranges } = getRelevantDateRange()

      const allSessions: Array<{
        center_id: string
        center_name: string
        periode: string
        semaine: string
        start_date: string
        end_date: string
        status: 'en_cours' | 'terminee' | 'a_venir'
      }> = []

      for (const range of ranges) {
        const { data, error } = await sansJpa(supabase
          .from('sessions')
          .select('id, center_id, start_date, end_date, periode, semaine, centers!inner(name)')
          .eq('active', true)
          .eq('archived', false)
          .gte('start_date', range.start)
          .lte('start_date', range.end), await stagesDesJpa())

        if (error) {
          console.error('Error fetching sessions:', error)
          continue
        }

        if (data) {
          for (const s of data) {
            const centerData = s.centers as unknown as { name: string }
            allSessions.push({
              center_id: s.center_id,
              center_name: centerData.name,
              periode: s.periode,
              semaine: s.semaine || '',
              start_date: s.start_date,
              end_date: s.end_date,
              status: range.status,
            })
          }
        }
      }

      // Group by center + semaine + status
      const grouped = new Map<string, SessionTile>()
      for (const s of allSessions) {
        const key = `${s.center_id}-${s.semaine}-${s.status}`
        const existing = grouped.get(key)
        if (existing) {
          existing.sessionCount++
        } else {
          grouped.set(key, {
            centerId: s.center_id,
            centerName: s.center_name,
            periode: s.periode,
            semaine: s.semaine,
            startDate: s.start_date,
            endDate: s.end_date,
            status: s.status,
            sessionCount: 1,
          })
        }
      }

      const result = Array.from(grouped.values())
      result.sort((a, b) => {
        if (a.status !== b.status) {
          const order = { a_venir: 0, en_cours: 1, terminee: 2 }
          return order[a.status] - order[b.status]
        }
        return a.centerName.localeCompare(b.centerName)
      })

      setTiles(result)
    } catch (err) {
      console.error('Error loading dashboard sessions:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleTileClick = (tile: SessionTile) => {
    if (!onNavigate) return
    const year = tile.startDate.split('-')[0]
    const targetPage = userProfile?.role === 'admin' ? 'admin-presences' : 'center-presences'
    onNavigate(targetPage, {
      centerId: tile.centerId,
      year,
      periode: tile.periode,
      semaine: tile.semaine,
    })
  }

  const handleKidsTileClick = (centerId: string) => {
    if (!onNavigate) return
    const targetPage = userProfile?.role === 'admin' ? 'kids-view' : 'center-kids'
    onNavigate(targetPage, { centerId })
  }

  const uniqueCenters = useMemo(() => {
    const seen = new Map<string, { id: string; name: string }>()
    for (const t of tiles) {
      if (!seen.has(t.centerId)) {
        seen.set(t.centerId, { id: t.centerId, name: t.centerName })
      }
    }
    return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name))
  }, [tiles])

  const getStatusConfig = (status: SessionTile['status']) => {
    switch (status) {
      case 'en_cours':
        return {
          label: 'En cours',
          bgColor: 'bg-blue-50 border-blue-200',
          badgeBg: 'bg-blue-100 text-blue-700',
          dotColor: 'bg-blue-500',
          iconColor: 'text-blue-500',
        }
      case 'terminee':
        return {
          label: 'Terminee',
          bgColor: 'bg-gray-50 border-gray-200',
          badgeBg: 'bg-gray-100 text-gray-600',
          dotColor: 'bg-gray-400',
          iconColor: 'text-gray-400',
        }
      case 'a_venir':
        return {
          label: 'A venir',
          bgColor: 'bg-emerald-50 border-emerald-200',
          badgeBg: 'bg-emerald-100 text-emerald-700',
          dotColor: 'bg-emerald-500',
          iconColor: 'text-emerald-500',
        }
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Tableau de bord</h1>
        <p className="text-gray-600 text-sm sm:text-base">Acces rapide a vos activites</p>
      </div>

      {/* Quick access: la garderie des écoles, les jours d'école */}
      {garderies.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
            <div className="flex items-center space-x-2">
              <School className="h-5 w-5 text-amber-600" />
              <h2 className="text-lg font-semibold text-gray-900">Garderie des écoles</h2>
            </div>
            <p className="text-sm text-gray-500 mt-1 first-letter:uppercase">{dateEnClair(garderies[0].date)} : la liste de la plage en cours, en un geste</p>
          </div>
          <div className="p-4 sm:p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {garderies.map((g) => (
              <button
                key={g.centerId}
                onClick={() => ouvrirLaGarderie(g)}
                className="relative text-left p-4 rounded-xl border-2 bg-amber-50 border-amber-200 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 group"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">
                    <span className="w-1.5 h-1.5 rounded-full mr-1.5 bg-amber-500" />
                    {g.tag}
                  </span>
                  <span className="text-lg font-bold text-gray-700">{g.plage.libelle}</span>
                </div>
                <div className="flex items-start space-x-2 mb-2">
                  <MapPin className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                  <span className="text-sm font-semibold text-gray-900 leading-tight">{g.nom}</span>
                </div>
                <div className="flex items-center space-x-2 mb-3">
                  <Clock className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <span className="text-sm text-gray-600">
                    {g.plage.heureDebut}–{g.plage.heureFin}
                  </span>
                </div>
                <div className="flex items-center justify-end pt-2 border-t border-amber-200/60">
                  <span className="flex items-center text-xs font-medium text-amber-700 group-hover:translate-x-0.5 transition-transform">
                    Pointer <ArrowRight className="h-3.5 w-3.5 ml-1" />
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Quick access: Presences */}
      {!(garderies.length > 0 && !loading && tiles.length === 0) && (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <Calendar className="h-5 w-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">Mes presences</h2>
          </div>
          <p className="text-sm text-gray-500 mt-1">Acces rapide aux listes de presences de la semaine</p>
        </div>

        <div className="p-4 sm:p-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
              <span className="ml-2 text-sm text-gray-500">Chargement des sessions...</span>
            </div>
          ) : tiles.length === 0 ? (
            <div className="text-center py-8">
              <Calendar className="h-10 w-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">Aucune session active cette semaine</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {tiles.map((tile) => {
                const config = getStatusConfig(tile.status)
                return (
                  <button
                    key={`${tile.centerId}-${tile.semaine}-${tile.status}`}
                    onClick={() => handleTileClick(tile)}
                    className={`relative text-left p-4 rounded-xl border-2 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 ${config.bgColor} group`}
                  >
                    {/* Status badge */}
                    <div className="flex items-center justify-between mb-3">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${config.badgeBg}`}>
                        <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${config.dotColor}`} />
                        {config.label}
                      </span>
                      <span className="text-lg font-bold text-gray-700">{tile.semaine}</span>
                    </div>

                    {/* Center name */}
                    <div className="flex items-start space-x-2 mb-2">
                      <MapPin className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <span className="text-sm font-semibold text-gray-900 leading-tight">{tile.centerName}</span>
                    </div>

                    {/* Dates */}
                    <div className="flex items-center space-x-2 mb-3">
                      <Clock className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <span className="text-sm text-gray-600">
                        Du {formatShortDate(tile.startDate)} au {formatShortDate(tile.endDate)}
                      </span>
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between pt-2 border-t border-gray-200/60">
                      <span className="text-xs text-gray-500">
                        {tile.sessionCount} {tile.sessionCount > 1 ? 'stages' : 'stage'}
                      </span>
                      <span className={`flex items-center text-xs font-medium ${config.iconColor} group-hover:translate-x-0.5 transition-transform`}>
                        Ouvrir <ArrowRight className="h-3.5 w-3.5 ml-1" />
                      </span>
                    </div>
                  </button>
                )
              })}
            </div>
          )}
        </div>
      </div>
      )}
      {/* Quick access: Infos enfants */}
      {!loading && uniqueCenters.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
            <div className="flex items-center space-x-2">
              <Users className="h-5 w-5 text-teal-600" />
              <h2 className="text-lg font-semibold text-gray-900">Infos enfants</h2>
            </div>
            <p className="text-sm text-gray-500 mt-1">Consulter les fiches des enfants inscrits par centre</p>
          </div>

          <div className="p-4 sm:p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {uniqueCenters.map((center) => (
                <button
                  key={center.id}
                  onClick={() => handleKidsTileClick(center.id)}
                  className="relative text-left p-4 rounded-xl border-2 bg-teal-50 border-teal-200 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 group"
                >
                  <div className="flex items-start space-x-2 mb-3">
                    <MapPin className="h-4 w-4 text-teal-500 mt-0.5 flex-shrink-0" />
                    <span className="text-sm font-semibold text-gray-900 leading-tight">{center.name}</span>
                  </div>
                  <div className="flex items-center justify-end pt-2 border-t border-teal-200/60">
                    <span className="flex items-center text-xs font-medium text-teal-600 group-hover:translate-x-0.5 transition-transform">
                      Voir les enfants <ArrowRight className="h-3.5 w-3.5 ml-1" />
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
